import React, { useEffect, useRef, useState } from 'react';
import { Languages, ChevronDown, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './LanguageSelector.css';

type SupportedLanguage = 'en' | 'zh' | 'ja' | 'ko' | 'fr' | 'de' | 'es' | 'ru';

interface LanguageSelectorProps {
  value?: SupportedLanguage;
  onChange?: (lang: SupportedLanguage) => void;
}

const languages = [
  { code: 'en' as const, name: 'English', nativeName: 'English' },
  { code: 'zh' as const, name: 'Chinese', nativeName: '中文' },
  { code: 'ja' as const, name: 'Japanese', nativeName: '日本語' },
  { code: 'ko' as const, name: 'Korean', nativeName: '한국어' },
  { code: 'fr' as const, name: 'French', nativeName: 'Français' },
  { code: 'de' as const, name: 'German', nativeName: 'Deutsch' },
  { code: 'es' as const, name: 'Spanish', nativeName: 'Español' },
  { code: 'ru' as const, name: 'Russian', nativeName: 'Русский' },
];

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ value, onChange }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentLanguage = value || i18n.language as SupportedLanguage;
  const currentLangInfo = languages.find(lang => lang.code === currentLanguage) || languages[0];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLanguageChange = (langCode: SupportedLanguage) => {
    if (onChange) {
      onChange(langCode);
    } else {
      i18n.changeLanguage(langCode);
    }
    setOpen(false);
  };

  return (
    <div className="relative ml-2" ref={containerRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`
          language-selector-button
          flex items-center gap-2 px-2 py-1.5 
          bg-white hover:bg-gray-50
          border border-gray-200 
          rounded-lg shadow-sm hover:shadow
          transition-all duration-200 ease-in-out
          text-xs font-medium text-gray-600
          min-w-fit
          ${open ? 'ring-1 ring-gray-300 shadow' : ''}
        `}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-mono text-gray-500 bg-gray-100 px-1 py-0.5 rounded text-[10px]">
            {currentLangInfo.code.toUpperCase()}
          </span>
          <span className="font-medium text-gray-700 text-xs">{currentLangInfo.nativeName}</span>
        </div>
        <ChevronDown 
          className={`h-2.5 w-2.5 text-gray-400 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {open && (
        <div className={`
          language-selector-dropdown
          absolute right-0 mt-1 
          bg-white border border-gray-200
          rounded-lg shadow-lg 
          w-40 z-50 
          max-h-60 overflow-y-auto
          animate-in fade-in-0 zoom-in-95 duration-200
        `}>
          <div className="p-1">
            {languages.map((lang, index) => (
              <button
                key={lang.code}
                className={`
                  language-option
                  w-full text-left px-2 py-1.5 rounded-md
                  flex items-center justify-between
                  transition-all duration-150 ease-in-out
                  text-gray-700
                  ${currentLanguage === lang.code 
                    ? 'bg-gray-100 text-gray-900' 
                    : 'hover:bg-gray-50'
                  }
                `}
                onClick={() => handleLanguageChange(lang.code)}
                role="option"
                aria-selected={currentLanguage === lang.code}
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-mono text-gray-500 bg-gray-100 px-1 py-0.5 rounded text-[10px]">
                    {lang.code.toUpperCase()}
                  </span>
                  <div className="font-medium text-xs text-gray-800">{lang.nativeName}</div>
                </div>
                <div className="flex items-center gap-2">
                  {currentLanguage === lang.code && (
                    <Check className="h-2.5 w-2.5 text-gray-600" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;